import { forEach } from 'lodash';

import { OwnedObject, flatten, flatMap, invokeMap$ } from 'gear';

import { Metadata, Xml } from '../util';

import { Figure, Figures, updateMethods, updateArray$ } from '../figure';

const editContentAlpha = 0.3;

export const Layer = OwnedObject.extend( updateMethods(), { typeName: 'Layer', __debugProps: true,

  Properties: {
    id      : { type:'string' , def: null, dropDef: true },
    name    : { type:'string' , def: null, dropDef: true },
    locked  : { type:'boolean', def:false, dropDef: true },
    hidden  : { type:'boolean', def:false, dropDef: true, onChange() { this.invalidateView(); } },
    xType   : { type: 'string', def: null, dropDef: true },
    figures : { type: 'Figure[]', def: [],
      onChange( added,removed) {
        this.lock();
        const doc = this.document;
        if( doc ) {
          doc.fireEvent('figureschanged',doc,added,removed);
        }
      },
      manualParsing: true
    },
    metadata: { type: 'Metadata', def: new Metadata({}), manualParsing: true }
  },

  get isLayer() {
    return true;
  },

  get document() {
    const { page } = this;
    return page && page.document;
  },

  get page() {
    return this._owner;
  },

  isDefault() {
    return !this._id && !this._name && !this._xType && !this._locked && !this._hidden &&
           this._metadata.isEmpty;
  },

  get visible() {
    return !this.hidden;
  },
  set visible( visible_ ) {
    this.hidden = !visible_ ;
  },

  get active() {},

  buildXml( node ) {
    return Xml.addObject(node,this,'Layer');
  },

  setupXml( node ) {
    Xml.setupProperties(node,this);
    this._metadata.setupXml(node);
    const figuresNode = Xml.appendElement(node,'Figures') ;
    Xml.addObjectArray(figuresNode,this._figures);
    return node;
  },

  add( figures ) {
    return this._figures.add(figures);
  },

  remove( figures ) {
    return this._figures.remove(figures);
  },

  replace( old, newFigures ) {
    return this._figures.replace(old,newFigures);
  },

  release( figures ) {
    return figures===undefined ? this._figures.clear()
                               : this._figures.remove(figures);
  },

  lock( originator ) {
    if( this._owner ) {
      this._owner.lock(originator||this);
    }
  },

  update$( type ) {
    return updateArray$(this._figures,type);
  },

  forEachFigure( func ) {
    return forEach( this.figures, func );
  },

  bounds$() {
    return Figures.bounds$(this._figures);
  },

  prepareToDraw$( zoom ) {
    return invokeMap$(this.figures,'prepareToDraw$',zoom);
  },

  spawnContentLayersFor( figure, point ) {
    const Cx_CL = Layer.ContentLayer, _this = this, figures = _this.release();
    const i = figures.indexOf(figure);

    // Save a partial version of the original figure that will be used to assemble back
    // the original one. The content layer create function can steal figure content where
    // appropriate
    this.figures = [ figure ];

    const background = Cx_CL.Background({ original: _this, figures:figures.slice(0,i) }),
          content    = Cx_CL.extract(_this,figure,point),
          foreground = Cx_CL.Foreground({ original: _this, figures:figures.slice(i+1) });

    return {
      layers: flatten([ background, content.layers, foreground ]),
      active: content.main
    };
  },

  beforeDraw( context, config ) {
    if( config.editingContent ) {
      context.globalAlpha *= editContentAlpha;
    }
  },

  afterDraw( context, config ) {
    if( config.editingContent ) {
      context.globalAlpha /= editContentAlpha;
    }
  }
});

const LayerType = function( node ) {
  const ct = node.getAttribute('ContentType');
  if( ct ) {
    return Layer.ContentLayer[ct];
  }
  return Layer;
};

Layer.parseXml = function(node,bcFixes) {
  const p = Xml.parseProperties(node,Layer);
  const figuresNode = Xml.child(node,'Figures');
  p.figures = figuresNode ? Figure.arrayFromXml(figuresNode,bcFixes) : [] ;
  const layer = new LayerType(node)(p);
  layer._metadata = Metadata.parseXml(node,layer);
  return layer;
};

Layer.arrayFromXml = function(node,bcFixes) {
  const layers = [];
  let notLayeredfigures = [];

  forEach( Xml.children_(node), child => {
    if( child.nodeName === 'Layer' ) {
      if( notLayeredfigures.length > 0 ) {
        // Create a new unnamed layer
        layers.push( new Layer({ figures: notLayeredfigures }) );
        notLayeredfigures = [];
      }
      layers.push( Layer.parseXml(child,bcFixes) );
    } else {
      const figure = Figure.parseXml(child,bcFixes);
      if( figure ) {
        notLayeredfigures.push(figure);
      }
    }
  });
  if( notLayeredfigures.length > 0 || layers.length === 0 ) { // There is always an implicit layer, even if there are no figures on it
    layers.push( new Layer({ figures: notLayeredfigures }) ) ;
  }
  return layers;
};

export const Layers = {};

Layers.flattenRelease = function(layers) {
  return flatMap( layers, layer => layer.release() );
};

Layers.flatten = function(layers) {
  return flatMap( layers, 'figures' );
};

Layers.bounds$ = function(layers) {
  return Figures.bounds$( Layers.flatten(layers) );
};

Layer.arrayToXmlString = function(layers) {
  const xml  = Xml.fromString('<Cdl></Cdl>'),
        root = xml.documentElement;
  // root.setAttribute('xmlns', 'http://www.vectorx.com/2007/Cdl');
  forEach( layers, layer => {
    layer.buildXml(root);
  });
  return Xml.toString(xml);
};


export function topTargetLayer( layers ) {
  for( let k = 0, kEnd = layers.length; k < kEnd; ++k ) {
    const layer = layers[k];
    if( ! ( layer._locked || layer._hidden )  ) {
      return layer;
    }
  }
}

export function filterTargetLayers( layers ) {
  const r = [];
  for( let k = 0, kEnd = layers.length; k < kEnd; ++k ) {
    const layer = layers[k];
    if( ! ( layer._locked || layer._hidden )  ) {
      r.push( layer );
    }
  }
  return r;
}



/** WEBPACK FOOTER **
 ** ./src/cdl/document/layer.js
 **/